<template>
  <div class="mt-5">
    <table class="b2b-grid b2b-grid--collapse is-compact">
      <!-- SKU, QTY Grid Headers -->
      <thead class="b2b-grid__header">
        <tr>
          <th class="b2b-grid__cell b2b-grid__head w-4">
            <span qaid="sku-grid-checkbox" @click.prevent.stop="checkAll">
              <grid-checkbox :label="'baseProduct.baseProduct'" :checked="isAllChecked" />
            </span>
          </th>
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid-sku" v-html="bp('sku')" />
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid-description" v-html="bp('description')" />
          <!-- Variable warehouse headers -->
          <th
            v-for="(warehouse, warehouseKey) in warehouseList"
            :key="`warehouse-${warehouseKey}`"
            class="b2b-grid__cell b2b-grid__head text-right"
            qaid="sku-grid-warehouse"
            v-html="warehouse"
          />
          <!-- </template> -->
          <th class="b2b-grid__cell b2b-grid__head text-center" qaid="sku-grid-stock-watch" v-html="bp('stockWatch')" />
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid-order-price" v-html="bp('price')" />
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid-qty" v-html="bp('qty')" />
          <th class="b2b-grid__cell b2b-grid__head" qaid="sku-grid-subtotal" v-html="bp('subtotal')" />
        </tr>
      </thead>

      <!-- SKU/QTY Grid Body/Rows -->
      <tbody>
        <!-- For each Product/Row -->
        <template v-for="product in paginatedData">
          <check-skus-grid-item
            :key="product.code"
            :locale="locale"
            :product="product"
            :checkbox-list="checkboxList"
            :base-properties="baseProperties"
            :update-check-all="updateCheckAll"
            :currency-iso-code="currencyISOCode"
            :add-to-checkbox-list="addToCheckboxList"
            :remove-from-checkbox-list="removeFromCheckboxList"
            :warehouse-codes="warehouseList"
            @update-variant-quantity="updateVariantQty"
            @update-stock-watch-state="(payload) => $emit('update-stock-watch-state', payload)"
          />
        </template>
      </tbody>
    </table>

    <!-- SKU Grid footer buttons (move to list/add to cart) -->
    <div class="buttons buttons--right-for-md">
      <trek-button
        primary
        small
        icon="shopping_cart"
        :disabled="isButtonDisabled"
        qaid="check-skus-add-to-cart"
        @click="addSkusToCart"
      >
        <span v-html="bp('addToCart')" />
      </trek-button>
      <trek-button
        id="saved-lists-button-accessories"
        v-tippy="{
          html: '#saved-lists-panel',
          interactive: true,
          reactive: true,
          trigger: 'click',
          flip: true,
          sticky: true,
        }"
        small
        secondary
        icon="list"
        qaid="check-skus-save-to-list"
        :disabled="isButtonDisabled"
        @click="updateSavedList"
      >
        <span v-html="bp('addToSavedList')" />
      </trek-button>

      <saved-lists-panel
        id="saved-lists-panel"
        :on-undo-callback="undoRemove"
        :on-submit-callback="removeProducts"
        :text-undo="bp('undo')"
        :text-my-lists="bp('myLists')"
        :text-max-chars="bp('max40Char')"
        :text-other-lists="bp('otherLists')"
        :text-moved-to-list="bp('moveToList')"
        :base-properties="baseProperties"
        qaid-panel="saved-lists-panel"
        qaid-title="saved-lists-panel-title"
        qaid-list="saved-lists-panel-list"
        btn-id="saved-lists-button-accessories"
      />
    </div>

    <div class="flex flex-wrap mb-1 pb-5">
      <div class="w-full lg:w-auto lg:flex-shrink self-center text-center pb-5">
        <pagination
          v-if="pageCount > 1"
          ref="pagination"
          :page-number="pageNumber"
          :page-count="pageCount"
          @page-change="pageChange"
          @prev-page="prevPage"
          @next-page="nextPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import pagination from '../../mixins/pagination';
import Pagination from '@/components/Pagination';
import TrekButton from '@/components/TrekButton';
import CheckSkusGridItem from './CheckSkusGridItem';
import GridCheckbox from '@/components/grid/GridCheckbox';
import SavedListsPanel from '@/components/saved-lists/SavedListsPanel';
import warehouseMixin from '../../mixins/warehouse';

export default {
  name: 'CheckSkuskGrid',

  components: {TrekButton, GridCheckbox, CheckSkusGridItem, SavedListsPanel, Pagination},

  mixins: [pagination, warehouseMixin],

  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    products: {
      type: Array,
      default: () => [],
      required: true,
    },
    size: {
      type: Number,
      required: false,
      default: 50,
    },
  },

  data() {
    return {
      checkboxList: [],
      isAllChecked: false,
      locale: '',
      currencyISOCode: '',
    };
  },

  computed: {
    isButtonDisabled() {
      return this.checkboxList.length === 0;
    },

    listData() {
      return this.products;
    },

    // Returns a list of Warehouse Header/Label names based on the products returned
    // Assumes that all products have the same warehouses in the same order
    warehouseList() {
      return this.getUniqueWarehouseCodesFromAllProductVariants(this.products);
    },

    ...mapState('miniCart', ['bikes', 'parts']),
  },

  watch: {
    checkboxList() {
      if (this.checkboxList.length === 0) {
        this.isAllChecked = false;
      }
    },

    'bikes.quantity': function() {
      this.removeAddedToCartCheckboxes();
    },

    'parts.quantity': function() {
      this.removeAddedToCartCheckboxes();
    },

    listData() {
      this.pageNumber = 0;
    },
  },

  created() {
    this.locale = window.location.pathname.split('/')[3].replace('_', '-');
    this.currencyISOCode = document.querySelector('#page').getAttribute('data-currency-iso-code');
  },

  methods: {
    removeAddedToCartCheckboxes() {
      const removeCheckboxes = this.checkboxList.filter((checkbox) => checkbox.isAllowedAddToCart === true);
      this.checkboxList = []; // uncheck all remained checkboxes(it will be all checkboxes that can't be added to cart)
      this.$emit('after-add-to-cart', removeCheckboxes);
    },

    addSkusToCart() {
      this.addToCart();
    },

    updateVariantQty(payload) {
      this.updateCheckboxList(payload);
      this.updateVariantQuantity({
        sku: payload.sku || payload.code,
        qty: payload.qty,
        baseSku: payload.sku || payload.code,
        callback: () => '',
      });

      this.$emit('update-product-quantity', payload);
    },

    addToCheckboxList(sku, isAllowedAddToCart) {
      if (!this.checkboxList.some((item) => item.sku === sku)) {
        this.checkboxList.push({sku, isAllowedAddToCart});
      }
    },

    undoRemove() {
      this.$emit('undo-saved-lists');
    },

    removeProducts() {
      this.$emit('on-added-products', this.checkboxList);
      this.checkboxList = [];
      this.isAllChecked = false;
    },

    updateSavedList() {
      this.setEntries(this.checkboxList);
    },

    updateCheckboxList(selectedProduct) {
      this.checkboxList = this.checkboxList.map((product) => {
        if (product.sku === selectedProduct.sku) {
          return {
            ...product,
            qty: selectedProduct.qty,
          };
        }
        return product;
      });
      this.$emit('update-variant-quantity', selectedProduct);
    },

    removeFromCheckboxList(selectedProduct) {
      if (this.checkboxList.some((item) => item.sku === selectedProduct.sku)) {
        this.checkboxList = this.checkboxList.filter((item) => item.sku !== selectedProduct.sku);
      }
    },

    checkAll() {
      this.isAllChecked = !this.isAllChecked;
      if (this.isAllChecked) {
        this.checkboxList = [];
        this.products.forEach((item) => {
          this.checkboxList.push({
            sku: item.code,
            isAllowedAddToCart: item.isAllowedAddToCart,
          });
        });
      } else {
        this.checkboxList = [];
      }
    },

    updateCheckAll() {
      this.isAllChecked = this.checkboxList.length === this.products.length;
    },

    ...mapActions('skuGrid', ['addToCart']),
    ...mapMutations('skuGrid', ['updateVariantQuantity']),
    ...mapMutations('savedLists', ['setEntries']),
    ...mapState('backend', ['isDropShipFlagEnabled']),
  },
};
</script>
